import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { X, Plus, Trash2, Loader2, CalendarDays, CheckCircle2, AlertTriangle } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { fmtDate } from "@/lib/format";

const sel = "rounded-lg border border-[#E5E0D8] px-3 py-2 text-sm outline-none focus:border-[#144F75] bg-white";
const blankRow = { date: "", time: "", slots: [], loading: false, status: "", error: "" };

// Book one or many package sessions (different dates/times) in a single flow.
// Every slot is validated server-side (availability, room & provider conflicts,
// minimum notice). Unbooked sessions stay on the member's account.
export default function PackageSchedulerModal({ credit, onClose }) {
  const qc = useQueryClient();
  const eligible = credit.eligible_services || [];
  const [serviceId, setServiceId] = useState(eligible[0]?.id || "");
  const [rows, setRows] = useState([{ ...blankRow }]);
  const [booking, setBooking] = useState(false);
  const [remaining, setRemaining] = useState(credit.sessions_remaining);

  const setRow = (idx, patch) => setRows((rs) => rs.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
  const pendingRows = rows.filter((r) => r.status !== "booked");
  const readyCount = pendingRows.filter((r) => r.date && r.time).length;
  const bookedCount = rows.filter((r) => r.status === "booked").length;

  const loadSlots = async (idx, date) => {
    setRow(idx, { date, time: "", slots: [], loading: !!date });
    if (!date || !serviceId) return;
    try {
      const { data } = await api.get(`/services/${serviceId}/open-slots`, { params: { date } });
      setRow(idx, { slots: data.slots || [], loading: false, error: (data.slots || []).length ? "" : "No open times that day — try another date." });
    } catch {
      setRow(idx, { slots: [], loading: false, error: "Couldn't load times — try again." });
    }
  };

  const changeService = (sid) => {
    setServiceId(sid);
    setRows((rs) => rs.map((r) => (r.status === "booked" ? r : { ...blankRow })));
  };

  const bookAll = async () => {
    setBooking(true);
    let left = remaining;
    for (let i = 0; i < rows.length; i++) {
      const r = rows[i];
      if (r.status === "booked" || !r.date || !r.time) continue;
      if (left <= 0) {
        setRow(i, { status: "error", error: "No sessions left on this package." });
        continue;
      }
      try {
        await api.post("/bookings", {
          service_id: serviceId, date: r.date, start_time: r.time,
          credit_id: credit.id, origin_url: window.location.origin,
        });
        setRow(i, { status: "booked", error: "" });
        left -= 1;
        setRemaining(left);
      } catch (err) {
        const d = err.response?.data?.detail;
        setRow(i, { status: "error", error: (d && (d.message || formatApiError(d))) || "Couldn't book this time — please pick another slot." });
      }
    }
    setBooking(false);
    qc.invalidateQueries({ queryKey: ["my-package-credits"] });
    qc.invalidateQueries({ queryKey: ["my-bookings"] });
  };

  const svcTitle = eligible.find((s) => s.id === serviceId)?.title || "session";

  return (
    <div className="fixed inset-0 z-[140] grid place-items-center px-4 bg-black/40 backdrop-blur-sm" data-testid="package-scheduler-modal">
      <div className="relative w-full max-w-lg bg-[#F9F7F3] rounded-3xl shadow-2xl p-8 max-h-[92vh] overflow-y-auto">
        <button onClick={onClose} data-testid="scheduler-close" className="absolute top-4 right-4 text-[#4A5D6B] hover:text-[#144F75]"><X size={20} /></button>
        <h3 className="font-serif text-xl text-[#1A242B]">Schedule your sessions</h3>
        <p className="text-sm text-[#4A5D6B] mt-1" data-testid="scheduler-remaining">
          {credit.package_name} · <b className="text-[#144F75]">{remaining}</b> session{remaining === 1 ? "" : "s"} remaining
        </p>

        {eligible.length > 1 && (
          <div className="mt-4">
            <label className="text-sm font-medium text-[#1A242B] block mb-1.5">Session type</label>
            <select value={serviceId} onChange={(e) => changeService(e.target.value)} data-testid="scheduler-service-select" className={`${sel} w-full`}>
              {eligible.map((s) => <option key={s.id} value={s.id}>{s.title} ({s.duration_min || 60} min)</option>)}
            </select>
          </div>
        )}

        <div className="mt-5 space-y-3">
          {rows.map((r, i) => (
            <div key={i} data-testid={`scheduler-row-${i}`}
              className={`rounded-xl border px-4 py-3 ${r.status === "booked" ? "border-[#5C8A72]/50 bg-[#5C8A72]/[0.07]" : r.status === "error" ? "border-red-300 bg-red-50/50" : "border-[#E5E0D8] bg-white"}`}>
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-xs font-semibold text-[#8FA89B] w-16">Session {i + 1}</span>
                {r.status === "booked" ? (
                  <span className="inline-flex items-center gap-1.5 text-sm text-[#5C8A72] font-medium" data-testid={`scheduler-booked-${i}`}>
                    <CheckCircle2 size={15} /> Booked — {fmtDate(r.date)} at {r.time}
                  </span>
                ) : (
                  <>
                    <input type="date" value={r.date} min={new Date().toISOString().slice(0, 10)}
                      onChange={(e) => loadSlots(i, e.target.value)} data-testid={`scheduler-date-${i}`} className={sel} />
                    {r.loading ? (
                      <Loader2 size={16} className="animate-spin text-[#144F75]" />
                    ) : (
                      <select value={r.time} onChange={(e) => setRow(i, { time: e.target.value })} disabled={!r.slots.length}
                        data-testid={`scheduler-time-${i}`} className={`${sel} disabled:opacity-50`}>
                        <option value="">{r.slots.length ? "Pick a time" : "Time"}</option>
                        {r.slots.map((t) => <option key={t} value={t}>{t}</option>)}
                      </select>
                    )}
                    {rows.length > 1 && (
                      <button onClick={() => setRows((rs) => rs.filter((_, j) => j !== i))} data-testid={`scheduler-remove-${i}`}
                        className="ml-auto text-[#8FA89B] hover:text-red-500"><Trash2 size={15} /></button>
                    )}
                  </>
                )}
              </div>
              {r.error && r.status !== "booked" && (
                <p className="text-xs text-red-600 mt-1.5 flex items-center gap-1" data-testid={`scheduler-error-${i}`}><AlertTriangle size={12} /> {r.error}</p>
              )}
            </div>
          ))}
        </div>

        {pendingRows.length < remaining && (
          <button onClick={() => setRows((rs) => [...rs, { ...blankRow }])} data-testid="scheduler-add-row"
            className="mt-3 inline-flex items-center gap-1.5 text-sm font-medium text-[#144F75] hover:underline">
            <Plus size={15} /> Add another session
          </button>
        )}

        <button onClick={bookAll} disabled={booking || readyCount === 0} data-testid="scheduler-book-all"
          className="eyw-btn w-full mt-5 disabled:opacity-60">
          {booking ? <Loader2 className="animate-spin" size={18} /> : <CalendarDays size={17} />}
          {readyCount > 0 ? `Book ${readyCount} ${svcTitle} session${readyCount === 1 ? "" : "s"}` : "Pick a date & time"}
        </button>

        <p className="text-xs text-[#8FA89B] mt-3 text-center">
          {bookedCount > 0 && <span className="text-[#5C8A72] font-medium">{bookedCount} booked. </span>}
          Unscheduled sessions stay on your account — book them anytime from your dashboard.
        </p>
        {remaining === 0 || (bookedCount > 0 && pendingRows.length === 0) ? (
          <button onClick={onClose} data-testid="scheduler-done" className="eyw-btn-ghost w-full mt-3 justify-center">Done</button>
        ) : null}
      </div>
    </div>
  );
}
